import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";

import { compose, graphql } from "lib/apolloClient";
import { useI18n } from "Hooks";

import { Modal, Button, ToggleSwitch } from "@toddle-edu/ds-web";

import { FormTextInput } from "UIComponents/InputTextField";
import { FormTextAreaInput } from "UIComponents/TextAreaInput";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader/GqlWithLoader";

import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";

import {
  ORGANIZATION,
  ROSTER_SYNC,
  ROSTER_SYNC_SUB_TYPES,
  USER,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants.js";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";

import classes from "./RosterSync.scss";

const RosterSyncWrapper = props => {
  const {
    orgId,
    updateAddOnStatus,
    internalToolGenericDataVariables,
    configValues,
    onSuccessAddOnConfigChange,
  } = props;
  const { t } = useI18n(["integration", "common"]);

  const [orgLevelToggleMap, setOrgLevelToggleMap] = useState({
    [ROSTER_SYNC_SUB_TYPES.BASE_SYNC]: _.get(
      configValues,
      `showSyncLinkV2`,
      false
    ),
    [ROSTER_SYNC_SUB_TYPES.LR_ARCH_ENABLE]: _.get(
      configValues,
      `newIntegrationLRArchitecture`,
      false
    ),
  });

  const [userLevelInputs, setUserLevelInputs] = useState({
    [ROSTER_SYNC_SUB_TYPES.SYNC_SETUP_BUTTON]: "",
    [ROSTER_SYNC_SUB_TYPES.BULK_MAPPING_BUTTON]: "",
  });

  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  const handleOrgLevelToggle = async ({ addOnSubType }) => {
    setFullScreenLoader(true);
    setOrgLevelToggleMap(prev => ({
      ...prev,
      [addOnSubType]: !prev[addOnSubType],
    }));
    await updateAddOnStatus({
      orgId,
      addOnType: ROSTER_SYNC,
      updateOn: ORGANIZATION,
      newStatus: !orgLevelToggleMap[addOnSubType],
      queryVariables: internalToolGenericDataVariables,
      addOnSubType,
    });
    setFullScreenLoader(false);
    if (!_.isNil(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
  };

  const handleUserLevelSubmit = async ({ addOnSubType }) => {
    const userIdsText = _.trim(userLevelInputs[addOnSubType]);
    if (_.isEmpty(userIdsText)) {
      return;
    }

    setFullScreenLoader(true);

    const userIds = _.filter(
      _.map(_.split(userIdsText, /[\n,]/), id => _.trim(id)),
      id => !_.isEmpty(id)
    );

    await updateAddOnStatus({
      orgId,
      addOnType: ROSTER_SYNC,
      updateOn: USER,
      newStatus: true,
      queryVariables: internalToolGenericDataVariables,
      addOnSubType,
      enabledUserIds: userIds,
      moduleId: addOnSubType,
    });

    setUserLevelInputs(prev => ({
      ...prev,
      [addOnSubType]: "",
    }));

    setFullScreenLoader(false);
  };

  const handleUserInputChange = ({ addOnSubType, value }) => {
    setUserLevelInputs(prev => ({
      ...prev,
      [addOnSubType]: value,
    }));
  };

  return (
    <div className={classes.container}>
      <FormTextInput
        size="large"
        label={t("common:org_id")}
        error=""
        value={orgId}
        isRequired={true}
        disabled={true}
      />

      <div className={classes.switchContainer}>
        <div>{t("integration:roster_sync_module")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={orgLevelToggleMap[ROSTER_SYNC_SUB_TYPES.BASE_SYNC]}
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: ROSTER_SYNC_SUB_TYPES.BASE_SYNC,
              })
            }
          />
        </div>
      </div>

      <div className={classes.switchContainer}>
        <div>{t("integration:lr_architecture_enable")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={orgLevelToggleMap[ROSTER_SYNC_SUB_TYPES.LR_ARCH_ENABLE]}
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: ROSTER_SYNC_SUB_TYPES.LR_ARCH_ENABLE,
              })
            }
          />
        </div>
      </div>

      <div className={classes.userInputSection}>
        <div className="text-label-l">{t("integration:sync_setup_button")}</div>
        <FormTextAreaInput
          size="large"
          label={t("common:user_ids")}
          placeholder={t("integration:enter_user_ids_placeholder")}
          value={userLevelInputs[ROSTER_SYNC_SUB_TYPES.SYNC_SETUP_BUTTON]}
          updateInputField={value =>
            handleUserInputChange({
              addOnSubType: ROSTER_SYNC_SUB_TYPES.SYNC_SETUP_BUTTON,
              value,
            })
          }
          minRows={2}
          maxRows={4}
        />
        <div>
          <Button
            _legacy_variant="outlined-subtle"
            variant="neutral"
            type="outlined"
            size="medium"
            onClick={() =>
              handleUserLevelSubmit({
                addOnSubType: ROSTER_SYNC_SUB_TYPES.SYNC_SETUP_BUTTON,
              })
            }
            disabled={_.isEmpty(
              _.trim(userLevelInputs[ROSTER_SYNC_SUB_TYPES.SYNC_SETUP_BUTTON])
            )}
          >
            {t("common:add")}
          </Button>
        </div>
      </div>

      <div className={classes.userInputSection}>
        <div className="text-label-l">
          {t("integration:bulk_mapping_upload_button")}
        </div>
        <FormTextAreaInput
          size="large"
          label={t("common:user_ids")}
          placeholder={t("integration:enter_user_ids_placeholder")}
          value={userLevelInputs[ROSTER_SYNC_SUB_TYPES.BULK_MAPPING_BUTTON]}
          updateInputField={value =>
            handleUserInputChange({
              addOnSubType: ROSTER_SYNC_SUB_TYPES.BULK_MAPPING_BUTTON,
              value,
            })
          }
          minRows={2}
          maxRows={4}
        />
        <div>
          <Button
            _legacy_variant="outlined-subtle"
            variant="neutral"
            type="outlined"
            size="medium"
            onClick={() =>
              handleUserLevelSubmit({
                addOnSubType: ROSTER_SYNC_SUB_TYPES.BULK_MAPPING_BUTTON,
              })
            }
            disabled={_.isEmpty(
              _.trim(userLevelInputs[ROSTER_SYNC_SUB_TYPES.BULK_MAPPING_BUTTON])
            )}
          >
            {t("common:add")}
          </Button>
        </div>
      </div>

      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const internalToolGenericDataVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: ROSTER_SYNC,
  });

  return {
    isData: true,
    isLoading: false,
    internalToolGenericDataVariables,
    orgId,
  };
};

const mapActionCreators = { updateAddOnStatus };

const RosterSyncWithLoader = compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    skip: ({ orgId }) => _.isEmpty(orgId),
    options: ({ internalToolGenericDataVariables }) => ({
      variables: internalToolGenericDataVariables,
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const data = _.get(getInternalToolGenericData, "platform");

      const queryData = _.get(data, "internalToolsGenericQuery.data", []);

      return {
        configValues: queryData,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getInternalToolGenericData.networkStatus
          ) || isLoading,
      };
    },
  }),
  withLoader
)(RosterSyncWrapper);

const RosterSync = ({
  orgId,
  closeModal,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n(["integration", "common"]);

  return (
    <Modal width="580px" height="700px" isOpen onClose={closeModal}>
      <Modal.Header title={t("integration:roster_sync")} isClosable />
      <Modal.Body isScrollable>
        <RosterSyncWithLoader
          onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
          closeModal={closeModal}
          orgId={orgId}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.rightFooterContainer}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              size="large"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RosterSync;
